import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Rating,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import { Send } from '@mui/icons-material';

const ServiceFeedback = () => {
  const [filters, setFilters] = useState({ search: '', rating: '' });
  const [form, setForm] = useState({
    customerName: '',
    customerId: '',
    serviceId: '',
    technicianName: '',
    technicianId: '',
    serviceType: '',
    serviceDate: '',
    rating: 4,
    quality: 'Good',
    behaviour: 'Polite',
    timeliness: 'On-time',
    comments: '',
    recommend: true,
    followUp: false,
    confirmed: false,
    photo: null,
  });

  const rows = useMemo(() => ([
    { id: 'SR-1002', customer: 'Rahul', project: 'Ground C2', rating: 5, comments: 'Great service!', date: '2025-10-29' },
    { id: 'SR-0998', customer: 'Amit', project: 'Battery B1', rating: 3, comments: 'Could be faster', date: '2025-10-26' },
  ]), []);

  const filtered = rows.filter(r => (!filters.search || `${r.id} ${r.customer} ${r.project}`.toLowerCase().includes(filters.search.toLowerCase())) && (!filters.rating || String(r.rating) === filters.rating));

  const projectNames = useMemo(() => [
    'Rooftop A1',
    'Ground C2',
    'Battery B1',
    'Plot X4',
  ], []);
  const serviceTypes = useMemo(() => [
    'Installation',
    'Maintenance',
    'AMC',
    'Repair',
    'Cleaning',
  ], []);

  const submitFeedback = () => {
    if (!form.serviceId || !form.customerName) { alert('Please fill Service ID and Customer Name'); return; }
    console.log('Feedback submit:', form);
    alert('Feedback submitted (demo)');
    setForm({
      customerName: '', customerId: '', serviceId: '', technicianName: '', technicianId: '', serviceType: '', serviceDate: '', rating: 4, quality: 'Good', behaviour: 'Polite', timeliness: 'On-time', comments: '', recommend: true, followUp: false, confirmed: false, photo: null,
    });
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>Service Feedback</Typography>
        <Typography variant="body2" color="text.secondary">Capture and view service feedback</Typography>
      </Box>

      {/* Feedback Form */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Customer Name" value={form.customerName} InputProps={{ readOnly: false }} onChange={(e) => setForm(prev => ({ ...prev, customerName: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Customer ID" value={form.customerId} onChange={(e) => setForm(prev => ({ ...prev, customerId: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Service ID" value={form.serviceId} onChange={(e) => setForm(prev => ({ ...prev, serviceId: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Project Name</InputLabel>
                <Select value={form.projectName || ''} label="Project Name" onChange={e => setForm(prev => ({ ...prev, projectName: e.target.value }))}>
                  <MenuItem value="">Select</MenuItem>
                  {projectNames.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={serviceTypes}
                freeSolo
                value={form.serviceType || ''}
                onChange={(_, value) => setForm(prev => ({ ...prev, serviceType: value }))}
                onInputChange={(_, value) => setForm(prev => ({ ...prev, serviceType: value }))}
                renderInput={params => <TextField {...params} label="Service Type (Suggest)" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Technician Name" value={form.technicianName} onChange={(e) => setForm(prev => ({ ...prev, technicianName: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Technician ID" value={form.technicianId} onChange={(e) => setForm(prev => ({ ...prev, technicianId: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" type="date" label="Service Date" InputLabelProps={{ shrink: true }} value={form.serviceDate} onChange={(e) => setForm(prev => ({ ...prev, serviceDate: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" sx={{ minWidth: 60 }}>Rating</Typography>
                <Rating value={form.rating} onChange={(_, v) => setForm(prev => ({ ...prev, rating: v || 0 }))} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Service Quality</InputLabel>
                <Select value={form.quality} label="Service Quality" onChange={(e) => setForm(prev => ({ ...prev, quality: e.target.value }))}>
                  <MenuItem value="Excellent">Excellent</MenuItem>
                  <MenuItem value="Good">Good</MenuItem>
                  <MenuItem value="Average">Average</MenuItem>
                  <MenuItem value="Poor">Poor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Technician Behaviour</InputLabel>
                <Select value={form.behaviour} label="Technician Behaviour" onChange={(e) => setForm(prev => ({ ...prev, behaviour: e.target.value }))}>
                  <MenuItem value="Polite">Polite</MenuItem>
                  <MenuItem value="Average">Average</MenuItem>
                  <MenuItem value="Rude">Rude</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Timeliness</InputLabel>
                <Select value={form.timeliness} label="Timeliness" onChange={(e) => setForm(prev => ({ ...prev, timeliness: e.target.value }))}>
                  <MenuItem value="On-time">On-time</MenuItem>
                  <MenuItem value="Late">Late</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Comments / Feedback" multiline rows={3} value={form.comments} onChange={(e) => setForm(prev => ({ ...prev, comments: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button variant="outlined" component="label">
                Upload Photo
                <input hidden type="file" accept="image/*" onChange={(e) => setForm(prev => ({ ...prev, photo: e.target.files && e.target.files[0] ? e.target.files[0] : null }))} />
              </Button>
              {form.photo && (
                <Typography variant="caption" sx={{ ml: 1 }}>{form.photo.name}</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" gap={2}>
                <FormControlLabel control={<Checkbox checked={form.confirmed} onChange={(e) => setForm(prev => ({ ...prev, confirmed: e.target.checked }))} />} label="Customer Confirmation" />
                <FormControlLabel control={<Checkbox checked={form.recommend} onChange={(e) => setForm(prev => ({ ...prev, recommend: e.target.checked }))} />} label="Would Recommend" />
                <FormControlLabel control={<Checkbox checked={form.followUp} onChange={(e) => setForm(prev => ({ ...prev, followUp: e.target.checked }))} />} label="Follow-up Needed" />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button fullWidth variant="contained" startIcon={<Send />} onClick={submitFeedback} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>Submit</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Search (SR # / Customer / Project)" value={filters.search} onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Rating</InputLabel>
                <Select value={filters.rating} label="Rating" onChange={(e) => setFilters(prev => ({ ...prev, rating: e.target.value }))}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="5">5</MenuItem>
                  <MenuItem value="4">4</MenuItem>
                  <MenuItem value="3">3</MenuItem>
                  <MenuItem value="2">2</MenuItem>
                  <MenuItem value="1">1</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Feedback Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>SR #</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Project</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Rating</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Comments</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{r.id}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.customer}</TableCell>
                    <TableCell>{r.project}</TableCell>
                    <TableCell><Rating value={r.rating} readOnly size="small" /></TableCell>
                    <TableCell>{r.comments}</TableCell>
                    <TableCell>{r.date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ServiceFeedback;



